import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { AuthProvider } from './context/AuthContext';
import { WebsiteSettingsProvider, useWebsiteSettings } from './context/WebsiteSettingsContext';
import { DataRefreshProvider } from './context/DataRefreshContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import './i18n/config';
import './App.css';

// Pages
import Landing from './pages/Landing';
import ProductList from './pages/ProductList';
import ProductDetails from './pages/ProductDetails';
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import OrderTracking from './pages/OrderTracking';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';

// Customer Pages
import Dashboard from './pages/customer/Dashboard';
import Profile from './pages/customer/Profile';
import OrderHistory from './pages/customer/OrderHistory';

// Common components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import FacebookPixel from './components/common/FacebookPixel';
import GoogleTagManager from './components/common/GoogleTagManager';
import TikTokPixel from './components/common/TikTokPixel';

// Static / Policy Pages
import AboutUs from './pages/static/AboutUs';
import Contact from './pages/static/Contact';
import FAQ from './pages/static/FAQ';
import ShippingPolicy from './pages/static/ShippingPolicy';
import ReturnsExchange from './pages/static/ReturnsExchange';
import TermsConditions from './pages/static/TermsConditions';
import PrivacyPolicy from './pages/static/PrivacyPolicy';

// Admin Components & Pages
import AdminRoute from './components/auth/AdminRoute';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import CategoryManagement from './pages/admin/CategoryManagement';
import ProductManagement from './pages/admin/ProductManagement';
import OrderManagement from './pages/admin/OrderManagement';
import CustomerManagement from './pages/admin/CustomerManagement';
import ReviewManagement from './pages/admin/ReviewManagement';
import CouponManagement from './pages/admin/CouponManagement';
import BannerManagement from './pages/admin/BannerManagement';
import DeliveryOptionsManagement from './pages/admin/DeliveryOptionsManagement';
import SEOManagement from './pages/admin/SEOManagement';
import ContentManagement from './pages/admin/ContentManagement';
import IntegrationsManagement from './pages/admin/IntegrationsManagement';
import FacebookPixelManagement from './pages/admin/FacebookPixelManagement';
import FacebookCAPIManagement from './pages/admin/FacebookCAPIManagement';
import GTMManagement from './pages/admin/GTMManagement';
import MetaAdsConfigPage from './pages/admin/MetaAdsConfig';
import MetaAdsReport from './pages/admin/MetaAdsReport';
import CampaignProducts from './pages/admin/CampaignProducts';
import TranslationManagement from './pages/admin/TranslationManagement';
import FulfillmentCenter from './pages/admin/FulfillmentCenter';
import WebsiteSettings from './pages/admin/WebsiteSettings';
import HomePageCMS from './pages/admin/HomePageCMS';
import AboutPageCMS from './pages/admin/AboutPageCMS';
import ContactPageCMS from './pages/admin/ContactPageCMS';
import FAQPageCMS from './pages/admin/FAQPageCMS';
import ShippingPageCMS from './pages/admin/ShippingPageCMS';
import ReturnsPageCMS from './pages/admin/ReturnsPageCMS';
import PolicyPageCMS from './pages/admin/PolicyPageCMS';
import AdminLogin from './pages/admin/AdminLogin';
import AdminProfile from './pages/admin/AdminProfile';
import TikTokPixelManagement from './pages/admin/TikTokPixelManagement';
import TikTokEventsAPIManagement from './pages/admin/TikTokEventsAPIManagement';

// Inner component that uses useLocation (must be inside Router)
function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const { settings, loading } = useWebsiteSettings();

  // Scroll to top on every route change (instant, bypasses smooth-scroll CSS)
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  React.useEffect(() => {
    // Remove the initial HTML loading spinner once React mounts
    const loader = document.getElementById('root-loader');
    if (loader) {
      loader.remove();
    }
  }, []);

  // Apply global browser-level settings from context (favicon, title, sessionStorage)
  React.useEffect(() => {
    if (!settings) return;

    // Save support phone
    if (settings.support_phone) {
      sessionStorage.setItem('support_phone', settings.support_phone);
    } else {
      sessionStorage.removeItem('support_phone');
    }

    // Save WhatsApp number
    if (settings.whatsapp_number) {
      sessionStorage.setItem('whatsapp_number', settings.whatsapp_number);
    } else {
      sessionStorage.removeItem('whatsapp_number');
    }

    // Save social media URLs
    if (settings.facebook_url) {
      sessionStorage.setItem('facebook_url', settings.facebook_url);
    } else {
      sessionStorage.removeItem('facebook_url');
    }

    if (settings.instagram_url) {
      sessionStorage.setItem('instagram_url', settings.instagram_url);
    } else {
      sessionStorage.removeItem('instagram_url');
    }

    if (settings.twitter_url) {
      sessionStorage.setItem('twitter_url', settings.twitter_url);
    } else {
      sessionStorage.removeItem('twitter_url');
    }

    if (settings.tiktok_url) {
      sessionStorage.setItem('tiktok_url', settings.tiktok_url);
    } else {
      sessionStorage.removeItem('tiktok_url');
    }
  }, [settings]);

  // Show nothing until settings are fetched (the HTML spinner covers this period)
  if (loading && !isAdminRoute) {
    return null;
  }

  const backgroundImage = settings?.background_image_url;

  return (
    <div
      className="App"
      style={{
        backgroundColor: 'white',
        ...(backgroundImage && {
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat'
        })
      }}
    >
      {!isAdminRoute && <FacebookPixel />}
      {!isAdminRoute && <TikTokPixel />}
      {!isAdminRoute && <GoogleTagManager />}
      {!isAdminRoute && <Navbar />}
      <main className={isAdminRoute ? 'admin-main-wrapper' : 'customer-main-wrapper'}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/products" element={<ProductList />} />
          <Route path="/products/:slug" element={<ProductDetails />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />
          <Route path="/track-order" element={<OrderTracking />} />

          {/* Static / Policy Pages */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/returns" element={<ReturnsExchange />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Admin Login Route (outside AdminRoute protection) */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Protected Customer Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-history"
            element={
              <ProtectedRoute>
                <OrderHistory />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="categories" element={<CategoryManagement />} />
              <Route path="products" element={<ProductManagement />} />
              <Route path="orders" element={<OrderManagement />} />
              <Route path="fulfillment" element={<FulfillmentCenter />} />
              <Route path="customers" element={<CustomerManagement />} />
              <Route path="reviews" element={<ReviewManagement />} />
              <Route path="coupons" element={<CouponManagement />} />
              <Route path="banners" element={<BannerManagement />} />
              <Route path="delivery-options" element={<DeliveryOptionsManagement />} />
              <Route path="seo" element={<SEOManagement />} />
              <Route path="content" element={<ContentManagement />} />
              <Route path="cms/home" element={<HomePageCMS />} />
              <Route path="cms/about" element={<AboutPageCMS />} />
              <Route path="cms/contact" element={<ContactPageCMS />} />
              <Route path="cms/faq" element={<FAQPageCMS />} />
              <Route path="cms/shipping" element={<ShippingPageCMS />} />
              <Route path="cms/returns" element={<ReturnsPageCMS />} />
              <Route path="cms/terms" element={<PolicyPageCMS pageType="terms" label="Terms & Conditions" icon="📜" />} />
              <Route path="cms/privacy" element={<PolicyPageCMS pageType="privacy" label="Privacy Policy" icon="🔒" />} />
              <Route path="settings" element={<WebsiteSettings />} />
              <Route path="integrations" element={<IntegrationsManagement />} />
              <Route path="pixel" element={<FacebookPixelManagement />} />
              <Route path="capi" element={<FacebookCAPIManagement />} />
              <Route path="gtm" element={<GTMManagement />} />
              <Route path="meta-ads/config" element={<MetaAdsConfigPage />} />
              <Route path="meta-ads/report" element={<MetaAdsReport />} />
              <Route path="meta-ads/products" element={<CampaignProducts />} />
              <Route path="translations" element={<TranslationManagement />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="tiktok-pixel" element={<TikTokPixelManagement />} />
              <Route path="tiktok-events-api" element={<TikTokEventsAPIManagement />} />
            </Route>
          </Route>
        </Routes>
      </main>
      {!isAdminRoute && <Footer />}
    </div>
  );
}

function App() {
  return (
    <DataRefreshProvider>
      <AuthProvider>
        <WebsiteSettingsProvider>
          <CartProvider>
            <WishlistProvider>
              <Router>
                <AppContent />
              </Router>
            </WishlistProvider>
          </CartProvider>
        </WebsiteSettingsProvider>
      </AuthProvider>
    </DataRefreshProvider>
  );
}

export default App;
